import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { ActionIcon, Badge, Checkbox, TextInput, Tooltip, UnstyledButton } from "@mantine/core"
import { IconCheck, IconPencil, IconX } from "@tabler/icons-react"
import { useInlineTermEdit } from "../../hooks/useInlineTermEdit"
import type { IJargonSuggestion } from "../../lib/api/jargon"
import type { IProps } from "./interfaces"
import classes from "./styles.module.css"

/**
 * JargonSuggestionList renders the shared per-term review rows behind both the
 * meeting summary's SuggestedJargonCard and the Jargon Manager's page list. Each
 * row is a candidate term (detected by the pipeline or captured from a transcript
 * correction) with click-to-edit spelling correction plus Accept (→ dictionary) /
 * Dismiss controls.
 *
 * Dumb component: props in, callbacks out — no data fetching, no company/auth
 * hooks. Local click-to-edit state lives in useInlineTermEdit. The host owns the
 * empty state and the surrounding chrome; this component only renders the rows,
 * styled by `variant` ("card" for the compact rail, "panel" for the full page).
 */
export function JargonSuggestionList({
  suggestions,
  onAccept,
  onDismiss,
  busyId = null,
  variant,
  selectedIds,
  onToggleSelect,
  exitingIds,
}: IProps): React.ReactNode {
  const { i18n } = useLingui()
  const { draft, setDraft, startEdit, cancelEdit, isEditing, acceptValue } = useInlineTermEdit()

  if (suggestions.length === 0) return null

  const isCard = variant === "card"
  // Selection is a panel-only affordance; the meeting-summary card never shows checkboxes.
  const selectable = !isCard && !!onToggleSelect

  const accept = (s: IJargonSuggestion): void => {
    if (isEditing(s.id) && draft.trim().length === 0) return
    const edited = acceptValue(s.id, s.term)
    if (edited) onAccept(s.id, edited)
    else onAccept(s.id)
    cancelEdit()
  }

  const rows = suggestions.map((s) => {
    const busy = busyId === s.id
    const editing = isEditing(s.id)

    // The term editor is shared verbatim; only the card variant re-styles the input.
    const termEditor = (
      <TextInput
        size="xs"
        value={draft}
        onChange={(e) => setDraft(e.currentTarget.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault()
            accept(s)
          } else if (e.key === "Escape") {
            e.preventDefault()
            cancelEdit()
          }
        }}
        onFocus={(e) => e.currentTarget.select()}
        autoFocus
        aria-label={i18n._(msg`Edit term`)}
        classNames={isCard ? { input: classes.cardEditInput } : undefined}
      />
    )

    // Accept / Dismiss actions are identical bar the Mantine variant + size the
    // two surfaces use.
    const acceptAction = (
      <Tooltip label={i18n._(msg`Add to dictionary`)} withArrow>
        <ActionIcon
          variant="subtle"
          color="success"
          size={isCard ? "sm" : "md"}
          className={classes.actionIcon}
          loading={busy}
          aria-label={i18n._(msg`Add ${s.term} to dictionary`)}
          onClick={() => accept(s)}
        >
          <IconCheck size={16} />
        </ActionIcon>
      </Tooltip>
    )
    const dismissAction = (
      <Tooltip label={editing ? i18n._(msg`Cancel`) : i18n._(msg`Dismiss`)} withArrow>
        <ActionIcon
          variant="subtle"
          color="danger"
          size={isCard ? "sm" : "md"}
          className={classes.actionIcon}
          disabled={busy}
          aria-label={editing ? i18n._(msg`Cancel editing ${s.term}`) : i18n._(msg`Dismiss ${s.term}`)}
          onClick={() => (editing ? cancelEdit() : onDismiss(s.id))}
        >
          <IconX size={16} />
        </ActionIcon>
      </Tooltip>
    )

    if (isCard) {
      return (
        <li key={s.id} className={classes.cardRow} data-busy={busy || undefined} aria-disabled={busy || undefined}>
          <div className={classes.cardInfo}>
            {editing ? (
              termEditor
            ) : (
              <button
                type="button"
                className={classes.cardTerm}
                onClick={() => startEdit(s.id, s.term)}
                aria-label={i18n._(msg`Edit suggested term ${s.term}`)}
                title={i18n._(msg`Click to edit`)}
                disabled={busy}
              >
                <span className={classes.cardTermText}>{s.term}</span>
                {s.frequency > 1 && <span className={classes.cardFreq}>×{s.frequency}</span>}
                <IconPencil size={11} className={classes.cardEditHint} />
              </button>
            )}
            {s.context && <span className={classes.cardContext}>{s.context}</span>}
          </div>
          <div className={classes.cardActions}>
            {acceptAction}
            {dismissAction}
          </div>
        </li>
      )
    }

    const selected = selectable ? (selectedIds?.has(s.id) ?? false) : false
    const exiting = exitingIds?.has(s.id) ?? false
    // The source is uniformly "detected" across the queue — only the rare
    // transcript-correction origin earns a badge; the common case stays quiet.
    const isCorrection = s.source === "transcript_edit"

    return (
      <div
        key={s.id}
        className={classes.panelRow}
        data-busy={busy || undefined}
        data-selected={selected || undefined}
        data-exiting={exiting || undefined}
        aria-disabled={busy || undefined}
      >
        {selectable && (
          <Checkbox
            checked={selected}
            onChange={() => onToggleSelect?.(s.id)}
            aria-label={i18n._(msg`Select ${s.term}`)}
            className={classes.panelCheckbox}
          />
        )}
        {editing ? (
          <div className={classes.panelEditWrap}>{termEditor}</div>
        ) : (
          <>
            <UnstyledButton
              className={classes.panelTerm}
              onClick={() => startEdit(s.id, s.term)}
              aria-label={i18n._(msg`Edit suggested term ${s.term}`)}
              title={i18n._(msg`Click to edit`)}
              disabled={busy}
            >
              <span className={classes.panelTermText}>{s.term}</span>
              <IconPencil size={12} className={classes.panelEditHint} />
            </UnstyledButton>
            {s.frequency > 1 && <span className={classes.panelFreq}>×{s.frequency}</span>}
            {isCorrection && (
              <Badge size="xs" variant="light" color="warning" className={classes.panelBadge}>
                <Trans>correction</Trans>
              </Badge>
            )}
            {s.context && (
              <span className={classes.panelContext} title={s.context}>
                {s.context}
              </span>
            )}
          </>
        )}
        <div className={classes.panelActions}>
          {acceptAction}
          {dismissAction}
        </div>
      </div>
    )
  })

  return isCard ? <ul className={classes.cardList}>{rows}</ul> : rows
}
